import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Share2, Heart, X, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { shareApp, dismissSharePrompt, HAPPY_EVENT } from '../utils/engagement';
import { trackEvent } from '../utils/analytics';
import { referralAPI } from '../utils/api';

// Global, one-time "love MoneyFeed? share it" prompt. Mounted once; opens when a
// happy-moment event fires (first export / 3rd receipt). Uses the native share sheet.
// Surfaces referral rewards (friends joined / bonus scans earned) to make inviting feel rewarding.
const ShareAppPrompt = () => {
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const onHappy = () => {
      setOpen(true);
      try { trackEvent('share_prompt_shown', {}); } catch { /* ignore */ }
      referralAPI.get()
        .then((res) => setStats(res.data))
        .catch(() => {});
    };
    window.addEventListener(HAPPY_EVENT, onHappy);
    return () => window.removeEventListener(HAPPY_EVENT, onHappy);
  }, []);

  const close = () => {
    dismissSharePrompt();
    setOpen(false);
  };

  const handleShare = async () => {
    const res = await shareApp();
    dismissSharePrompt();
    if (res === 'copied') toast.success('Invite copied — paste it to a friend!');
    setOpen(false);
  };

  const hasStats = stats && (stats.referral_count > 0 || stats.scans_earned > 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-sm text-center p-6" data-testid="share-app-prompt">
        <button onClick={close} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600" aria-label="Dismiss" data-testid="share-app-prompt-dismiss">
          <X className="h-4 w-4" />
        </button>
        <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-[#064E3B] to-[#84CC16] flex items-center justify-center mb-3">
          <Heart className="h-7 w-7 text-white" />
        </div>
        <DialogTitle className="text-lg font-bold text-gray-800 dark:text-white">Enjoying MoneyFeed?</DialogTitle>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">
          Invite a freelancer friend — you each get <span className="font-semibold text-[#064E3B] dark:text-[#84CC16]">+10 bonus receipt scans</span> when they join.
        </p>

        {hasStats && (
          <div className="flex items-center justify-center gap-5 mb-4 rounded-xl bg-[#F0FDF4] dark:bg-slate-800 border border-[#84CC16]/30 py-3" data-testid="share-prompt-reward-stats">
            <div>
              <div className="text-2xl font-bold text-[#064E3B] dark:text-[#84CC16]" data-testid="share-prompt-friends-count">{stats.referral_count}</div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">friends joined</div>
            </div>
            <div className="h-8 w-px bg-[#84CC16]/30" />
            <div>
              <div className="flex items-center justify-center gap-1 text-2xl font-bold text-[#064E3B] dark:text-[#84CC16]" data-testid="share-prompt-scans-earned">
                <Gift className="h-4 w-4" />{stats.scans_earned}
              </div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">bonus scans earned</div>
            </div>
          </div>
        )}

        <Button onClick={handleShare} className="w-full bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full" data-testid="share-app-prompt-share-btn">
          <Share2 className="h-4 w-4 mr-2" /> Invite friends
        </Button>
        <button onClick={close} className="mt-3 text-xs text-gray-400 hover:underline w-full" data-testid="share-app-prompt-later-btn">
          Maybe later
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default ShareAppPrompt;
